import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import PageLayout from '../components/PageLayout';

// Traductions pour la page About
const translations = {
  en: {
    title: "About",
    stats: {
      satisfaction: "Customer Satisfaction",
      professionals: "Dedicated Professionals",
      projects: "Projects Completed",
      experience: "Year of Experience"
    },
    sections: {
      history: {
        title: "Our History",
        content: "INO STUDIO was born from an idea between friends in 2024. What started as a passionate project quickly transformed into a real business. As we grew, we hired talented designers and expanded our team to offer comprehensive services to sellers and creators in the world of e-commerce and online presence."
      },
      services: {
        title: "Our Services",
        content: "We offer a complete range of services tailored to the needs of modern sellers:",
        list: [
          "Creation of customized, modern, and elegant websites",
          "Community management via Discord, TikTok, and Instagram",
          "Optimized order management system for online sellers",
          "Personalized service for sellers of accessories, luxury clothing, and streetwear brands"
        ]
      },
      team: {
        title: "Our Team",
        content: "We have a team of dedicated professionals working together to deliver exceptional results:",
        members: [
          {
            title: "2 Professional Designers",
            description: "Creators of impactful visuals and elegant interfaces"
          },
          {
            title: "1 Expert Community Manager",
            description: "Specialist in community animation and engagement"
          },
          {
            title: "1-2 Developers",
            description: "Code experts to create tailored technical solutions"
          }
        ]
      },
      approach: {
        title: "Our Approach",
        content: "We work closely with our clients, presenting our work and adapting our services to their specific needs. Our approach is based on dialogue and flexibility - we are here to support you according to your particular needs, without ever imposing services you don't need."
      },
      values: {
        title: "Our Values",
        content: "Customer satisfaction is at the heart of our mission. We value transparent communication and encourage our clients to share their ideas. We respect strict confidentiality clauses with our clients, which prevents us from specifically mentioning the sellers we work with, but this discretion is proof of our commitment to our partners' trust."
      }
    }
  },
  zh: {
    title: "关于我们",
    stats: {
      satisfaction: "客户满意度",
      professionals: "专业团队成员",
      projects: "已完成项目",
      experience: "年经验"
    },
    sections: {
      history: {
        title: "我们的历史",
        content: "INO STUDIO源于2024年朋友之间的一个想法。最初的激情项目迅速转变为一个真正的企业。随着我们的发展，我们聘请了有才华的设计师并扩大了团队，为电子商务和在线存在领域的卖家和创作者提供全面的服务。"
      },
      services: {
        title: "我们的服务",
        content: "我们提供一系列适合现代卖家需求的完整服务：",
        list: [
          "创建定制、现代和优雅的网站",
          "通过Discord、TikTok和Instagram进行社区管理",
          "为在线卖家优化订单管理系统",
          "为配饰、奢侈品服装和街头服饰品牌卖家提供个性化服务"
        ]
      },
      team: {
        title: "我们的团队",
        content: "我们拥有一支致力于提供卓越结果的专业团队：",
        members: [
          {
            title: "2位专业设计师",
            description: "有影响力的视觉和优雅界面的创造者"
          },
          {
            title: "1位专家社区经理",
            description: "社区活动和参与的专家"
          },
          {
            title: "1-2位开发人员",
            description: "创建定制技术解决方案的代码专家"
          }
        ]
      },
      approach: {
        title: "我们的方法",
        content: "我们与客户密切合作，展示我们的工作并根据其特定需求调整我们的服务。我们的方法基于对话和灵活性 - 我们根据您的特定需求为您提供支持，而不会强加您不需要的服务。"
      },
      values: {
        title: "我们的价值观",
        content: "客户满意度是我们使命的核心。我们重视透明沟通，并鼓励客户分享他们的想法。我们与客户遵守严格的保密条款，这使我们无法具体提及我们合作的卖家，但这种谨慎是我们对合作伙伴信任承诺的证明。"
      }
    }
  }
};

// Conteneur principal - ajusté pour déplacer le contenu vers le haut
const AboutContainer = styled.div`
  height: 100vh; /* Hauteur fixe */
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* Centre verticalement */
  padding: 0 5%;
  position: relative;
  overflow: hidden; /* Empêche le défilement */
  
  /* Déplacement de 100px vers le haut */
  transform: translateY(-50px);
`;

// Arrière-plan parallaxe
const ParallaxBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const ParallaxLayer = styled(motion.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const Circle = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  opacity: 0.4;
  background: ${props => props.color || 'rgba(255, 255, 255, 0.1)'};
  filter: blur(${props => props.blur || '0px'});
`;

// Titre
const Title = styled(motion.h1)`
  font-size: clamp(2.2rem, 6vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: 25px;
  text-align: center;
  position: relative;
  z-index: 2;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 2px;
  }
`;

// Conteneur principal de contenu
const MainContent = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-height: calc(100vh - 170px);
  overflow: visible;
`;

// Section de statistiques
const StatsSection = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  width: 100%;
  max-width: 900px;
  margin: 0 auto 25px;
  position: relative;
  z-index: 2;
`;

const StatCard = styled(motion.div)`
  background: rgba(40, 40, 40, 0.85);
  backdrop-filter: blur(5px);
  border-radius: 12px;
  padding: 20px;
  width: 180px;
  height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const StatIcon = styled.div`
  margin-bottom: 10px;
  font-size: 2rem;
  color: rgba(255, 255, 255, 0.9);
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  opacity: 0.8;
  text-align: center;
`;

// Icônes personnalisées simplifiées
const IconActivity = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
  </svg>
);

const IconUsers = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const IconCode = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6"></polyline>
    <polyline points="8 6 2 12 8 18"></polyline>
  </svg>
);

const IconAward = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="7"></circle>
    <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
  </svg>
);

const IconTarget = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <circle cx="12" cy="12" r="6"></circle>
    <circle cx="12" cy="12" r="2"></circle>
  </svg>
);

const IconHeart = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
  </svg>
);

const IconChevronDown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"></polyline>
  </svg>
);

const IconChevronUp = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="18 15 12 9 6 15"></polyline>
  </svg>
);

// Système d'accordéon
const ContentSection = styled(motion.div)`
  width: 100%;
  max-width: 800px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  z-index: 2;
  position: relative;
  max-height: calc(100vh - 330px);
  overflow-y: auto;
  
  /* Personnalisation de la scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.3);
  }
`;

const AccordionItem = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  &:last-child {
    border-bottom: none;
  }
`;

const AccordionHeader = styled.div`
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.03);
  transition: background 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

const AccordionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AccordionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
`;

const AccordionContent = styled(motion.div)`
  background: white;
  overflow: hidden;
`;

const AccordionInner = styled.div`
  padding: 15px 20px;
`;

const Paragraph = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  color: #333;
  margin-bottom: 15px;
  text-align: left;
  width: 100%;
`;

const ListContainer = styled.ul`
  width: 100%;
  padding-left: 20px;
  margin-bottom: 15px;
`;

const ListItem = styled.li`
  font-size: 0.95rem;
  line-height: 1.5;
  color: #333;
  margin-bottom: 8px;
  position: relative;
`;

const TeamSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  width: 100%;
  margin-top: 8px;
  margin-bottom: 15px;
`;

const TeamMember = styled.div`
  flex: 1;
  min-width: 180px;
  background: rgba(0, 0, 0, 0.05);
  padding: 12px;
  border-radius: 8px;
`;

const MemberTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 4px;
  color: #333;
`;

const MemberDescription = styled.p`
  font-size: 0.9rem;
  color: #555;
`;

// Componente pour l'espace de la navbar - pour éviter les chevauchements
const NavbarSpacer = styled.div`
  height: 70px; /* Ajustez en fonction de la hauteur de votre navbar */
  width: 100%;
`;

// Composant pour les nombres animés
const AnimatedCounter = ({ value, duration = 2 }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);

  useEffect(() => {
    // Fonction pour vérifier si l'élément est visible
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          // Animation des chiffres
          let start = 0;
          const end = parseInt(value);
          const incrementTime = Math.floor(duration * 1000 / end);
          
          // Plus rapide pour les petites valeurs
          const timer = setInterval(() => {
            start += 1;
            setCount(start);
            if (start >= end) clearInterval(timer);
          }, incrementTime > 10 ? incrementTime : 10);
          
          // Nettoyer l'observateur une fois l'animation déclenchée
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );

    if (countRef.current) {
      observer.observe(countRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [value, duration]);

  return <span ref={countRef}>{count}</span>;
};

const About = () => {
  const { i18n } = useTranslation();
  const [openSection, setOpenSection] = useState("history");
  
  // Obtenir la langue actuelle
  const currentLanguage = i18n.language || 'en';
  const content = translations[currentLanguage] || translations.en;
  
  // Désactiver le défilement de la page
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = '';
    };
  }, []);
  
  const toggleAccordion = (section) => {
    setOpenSection(openSection === section ? "" : section);
  };

  return (
    <PageLayout>
      <AboutContainer>
        {/* Arrière-plan parallaxe */}
        <ParallaxBackground>
          <ParallaxLayer>
            <Circle 
              color="rgba(25, 118, 210, 0.3)" 
              blur="80px"
              style={{ 
                width: "500px", 
                height: "500px", 
                top: "10%", 
                left: "5%" 
              }} 
            />
          </ParallaxLayer>
          <ParallaxLayer>
            <Circle 
              color="rgba(156, 39, 176, 0.3)" 
              blur="60px"
              style={{ 
                width: "350px", 
                height: "350px", 
                top: "20%", 
                right: "10%" 
              }} 
            />
          </ParallaxLayer>
          <ParallaxLayer>
            <Circle 
              color="rgba(255, 87, 34, 0.2)" 
              blur="70px"
              style={{ 
                width: "420px", 
                height: "420px", 
                bottom: "5%", 
                left: "30%" 
              }} 
            />
          </ParallaxLayer>
        </ParallaxBackground>

        {/* Titre */}
        <Title
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {content.title}
        </Title>
        
        <MainContent>
          {/* Section de statistiques */}
          <StatsSection
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <StatCard
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.4 }}
            >
              <StatIcon><IconActivity /></StatIcon>
              <StatNumber><AnimatedCounter value="100" duration={1.5} />%</StatNumber>
              <StatLabel>{content.stats.satisfaction}</StatLabel>
            </StatCard>
            
            <StatCard
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.4 }}
            >
              <StatIcon><IconUsers /></StatIcon>
              <StatNumber><AnimatedCounter value="5" duration={1} />+</StatNumber>
              <StatLabel>{content.stats.professionals}</StatLabel>
            </StatCard>
            
            <StatCard
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.4 }}
            >
              <StatIcon><IconCode /></StatIcon>
              <StatNumber><AnimatedCounter value="20" duration={1.5} />+</StatNumber>
              <StatLabel>{content.stats.projects}</StatLabel>
            </StatCard>
            
            <StatCard
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.4 }}
            >
              <StatIcon><IconAward /></StatIcon>
              <StatNumber><AnimatedCounter value="1" duration={0.8} />+</StatNumber>
              <StatLabel>{content.stats.experience}</StatLabel>
            </StatCard>
          </StatsSection>
          
          {/* Section de contenu avec accordéon */}
          <ContentSection
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            {/* Accordéon - Notre histoire */}
            <AccordionItem>
              <AccordionHeader onClick={() => toggleAccordion("history")}>
                <AccordionTitle>
                  <IconTarget /> {content.sections.history.title}
                </AccordionTitle>
                <AccordionIcon>
                  {openSection === "history" ? <IconChevronUp /> : <IconChevronDown />}
                </AccordionIcon>
              </AccordionHeader>
              <AnimatePresence>
                {openSection === "history" && (
                  <AccordionContent
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AccordionInner>
                      <Paragraph>
                        {content.sections.history.content}
                      </Paragraph>
                    </AccordionInner>
                  </AccordionContent>
                )}
              </AnimatePresence>
            </AccordionItem>
            
            {/* Accordéon - Nos services */}
            <AccordionItem>
              <AccordionHeader onClick={() => toggleAccordion("services")}>
                <AccordionTitle>
                  <IconActivity /> {content.sections.services.title}
                </AccordionTitle>
                <AccordionIcon>
                  {openSection === "services" ? <IconChevronUp /> : <IconChevronDown />}
                </AccordionIcon>
              </AccordionHeader>
              <AnimatePresence>
                {openSection === "services" && (
                  <AccordionContent
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AccordionInner>
                      <Paragraph>
                        {content.sections.services.content}
                      </Paragraph>
                      <ListContainer>
                        {content.sections.services.list.map((item, index) => (
                          <ListItem key={index}>{item}</ListItem>
                        ))}
                      </ListContainer>
                    </AccordionInner>
                  </AccordionContent>
                )}
              </AnimatePresence>
            </AccordionItem>
            
            {/* Accordéon - Notre équipe */}
            <AccordionItem>
              <AccordionHeader onClick={() => toggleAccordion("team")}>
                <AccordionTitle>
                  <IconUsers /> {content.sections.team.title}
                </AccordionTitle>
                <AccordionIcon>
                  {openSection === "team" ? <IconChevronUp /> : <IconChevronDown />}
                </AccordionIcon>
              </AccordionHeader>
              <AnimatePresence>
                {openSection === "team" && (
                  <AccordionContent
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AccordionInner>
                      <Paragraph>
                        {content.sections.team.content}
                      </Paragraph>
                      <TeamSection>
                        {content.sections.team.members.map((member, index) => (
                          <TeamMember key={index}>
                            <MemberTitle>{member.title}</MemberTitle>
                            <MemberDescription>{member.description}</MemberDescription>
                          </TeamMember>
                        ))}
                      </TeamSection>
                    </AccordionInner>
                  </AccordionContent>
                )}
              </AnimatePresence>
            </AccordionItem>
            
            {/* Accordéon - Notre approche */}
            <AccordionItem>
              <AccordionHeader onClick={() => toggleAccordion("approach")}>
                <AccordionTitle>
                  <IconTarget /> {content.sections.approach.title}
                </AccordionTitle>
                <AccordionIcon>
                  {openSection === "approach" ? <IconChevronUp /> : <IconChevronDown />}
                </AccordionIcon>
              </AccordionHeader>
              <AnimatePresence>
                {openSection === "approach" && (
                  <AccordionContent
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AccordionInner>
                      <Paragraph>
                        {content.sections.approach.content}
                      </Paragraph>
                    </AccordionInner>
                  </AccordionContent>
                )}
              </AnimatePresence>
            </AccordionItem>
            
            {/* Accordéon - Nos valeurs */}
            <AccordionItem>
              <AccordionHeader onClick={() => toggleAccordion("values")}>
                <AccordionTitle>
                  <IconHeart /> {content.sections.values.title}
                </AccordionTitle>
                <AccordionIcon>
                  {openSection === "values" ? <IconChevronUp /> : <IconChevronDown />}
                </AccordionIcon>
              </AccordionHeader>
              <AnimatePresence>
                {openSection === "values" && (
                  <AccordionContent
                    initial={{ height: 0 }}
                    animate={{ height: "auto" }}
                    exit={{ height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <AccordionInner>
                      <Paragraph>
                        {content.sections.values.content}
                      </Paragraph>
                    </AccordionInner>
                  </AccordionContent>
                )}
              </AnimatePresence>
            </AccordionItem>
          </ContentSection>
        </MainContent>
        
        {/* Espace pour éviter que le contenu ne chevauche la navbar */}
        <NavbarSpacer />
      </AboutContainer>
    </PageLayout>
  );
};

export default About;